import React, { useEffect, useRef, useCallback, useMemo, createContext, useContext } from "react";
import { v4 as uuidv4 } from "uuid";
import { MediaItemFile, MediaItem } from "@/types/media";
import { useMedia } from "@/contexts/MediaContext";
import {
    useComparisonSources,
    useComparisonLoading,
    useSetCurrentMedia,
    useCurrentMedia,
    useAddUploadedSource,
    useRunInferenceForSource
} from "@/store";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import type { TestSourceWithInference } from "../types";

/**
 * Download an image from a URL and convert it to a File object.
 * Uses the sourceId as the file.id to maintain consistency with the source document.
 * Sets skipUpload=true since these files are already hosted.
 */
async function downloadImageAsFile(
    url: string,
    filename: string,
    sourceId: string
): Promise<MediaItemFile> {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`Failed to download image: ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();
    const file = new File([blob], filename, { type: blob.type || "image/jpeg" }) as MediaItemFile;
    file.id = sourceId;
    file.skipUpload = true;
    file.imageUrl = url;
    return file;
}

interface ModelComparisonContextType {
    /** Get inference results for a source by its ID */
    getInferencesForSource: (sourceId: string) => TestSourceWithInference["inferences"] | undefined;
    /** Whether sources are being loaded into the media system */
    isLoadingSources: boolean;
    /** Handle uploaded files: add to media system, store, and run inference */
    handleUploadedFiles: (files: File[]) => Promise<void>;
}

const ModelComparisonContext = createContext<ModelComparisonContextType | null>(null);

export function useModelComparison() {
    const context = useContext(ModelComparisonContext);
    if (!context) {
        throw new Error("useModelComparison must be used within ModelComparisonProvider");
    }
    return context;
}

interface ModelComparisonProviderProps {
    children: React.ReactNode;
}

/**
 * Provider that bridges the model comparison flow with the existing media infrastructure.
 *
 * It watches `comparisonSources` from the model slice and:
 * 1. Converts them to MediaItems using the MediaContext
 * 2. Sets the first source as current media
 * 3. Provides access to inference results via context
 *
 * This allows the existing FileCarousel and FileSwitcher to work for navigation,
 * while a custom comparison preview component can render side-by-side predictions.
 */
export function ModelComparisonProvider({ children }: ModelComparisonProviderProps) {
    const comparisonSources = useComparisonSources();
    const comparisonLoading = useComparisonLoading();
    const { addMediaItems, completedMediaItems } = useMedia();
    const setCurrentMedia = useSetCurrentMedia();
    const currentMedia = useCurrentMedia();
    const { currentWorkspace, apiKey } = useCurrentWorkspace();
    const addUploadedSource = useAddUploadedSource();
    const runInferenceForSource = useRunInferenceForSource();

    const hasInitializedRef = useRef(false);
    // Track whether the store fetch has started at least once
    const hasSeenLoadingRef = useRef(false);
    // Start as true so there's no flash of upload CTA before sources finish loading into media
    const [isLoadingSources, setIsLoadingSources] = React.useState(true);

    // Track uploaded file IDs that are pending Firebase Storage upload completion
    const pendingUploadIdsRef = useRef<Set<string>>(new Set());

    // Track blob URLs created for uploaded files so we can revoke them on unmount
    const blobUrlsRef = useRef<Set<string>>(new Set());

    // Revoke all blob URLs on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            for (const url of blobUrlsRef.current) {
                URL.revokeObjectURL(url);
            }
            blobUrlsRef.current.clear();
        };
    }, []);

    // Build a map from sourceId to inferences for quick lookup
    const inferencesBySourceId = useMemo(() => {
        const map = new Map<string, TestSourceWithInference["inferences"]>();
        for (const source of comparisonSources) {
            map.set(source.id, source.inferences);
        }
        return map;
    }, [comparisonSources]);

    // Track when the store starts loading so we know when it's genuinely done
    useEffect(() => {
        if (comparisonLoading) {
            hasSeenLoadingRef.current = true;
        }
    }, [comparisonLoading]);

    // Stop loading if the store finished fetching but returned no sources
    useEffect(() => {
        if (
            hasSeenLoadingRef.current &&
            !comparisonLoading &&
            comparisonSources.length === 0 &&
            !hasInitializedRef.current
        ) {
            setIsLoadingSources(false);
        }
    }, [comparisonLoading, comparisonSources.length]);

    // Load comparison sources into the media system
    useEffect(() => {
        // Skip if already initialized, loading, or no sources
        if (hasInitializedRef.current || comparisonLoading || comparisonSources.length === 0) {
            return;
        }

        hasInitializedRef.current = true;
        setIsLoadingSources(true);

        const loadSources = async () => {
            try {
                let isFirstAdded = false;

                // Download all images in parallel, adding each to media as soon as it's ready
                await Promise.all(
                    comparisonSources.map(async (source) => {
                        try {
                            const file = await downloadImageAsFile(
                                source.imageUrl,
                                source.name || `${source.id}.jpg`,
                                source.id
                            );

                            const addedItems = await addMediaItems([file]);

                            // Set the first successfully loaded image as current media immediately
                            if (!isFirstAdded && addedItems && addedItems.length > 0) {
                                isFirstAdded = true;
                                const firstItem = addedItems[0];
                                setCurrentMedia({
                                    file: firstItem,
                                    frame: firstItem,
                                    frameSegment: 0
                                });
                                // Clear loading as soon as the first image is ready to display
                                setIsLoadingSources(false);
                            }
                        } catch (err) {
                            console.error(
                                `[ModelComparisonProvider] Failed to load ${source.id}:`,
                                err
                            );
                        }
                    })
                );
            } catch (err) {
                console.error("[ModelComparisonProvider] Error loading sources:", err);
            } finally {
                // Ensure loading is cleared even if no images succeeded
                setIsLoadingSources(false);
            }
        };

        loadSources();
    }, [comparisonSources, comparisonLoading, addMediaItems, setCurrentMedia]);

    // Watch for uploaded files completing their Firebase Storage upload.
    // When a tracked file finishes uploading, fire inference using its storage fileId.
    useEffect(() => {
        if (pendingUploadIdsRef.current.size === 0) return;

        for (const mediaItem of completedMediaItems) {
            if (!pendingUploadIdsRef.current.has(mediaItem.id)) continue;

            const fileId = mediaItem.uploadResult?.filename;
            if (!fileId) continue;

            // Remove from pending set so we don't fire twice
            pendingUploadIdsRef.current.delete(mediaItem.id);

            // Run inference via the standard api/infer SSE endpoint
            if (apiKey) {
                runInferenceForSource({ id: mediaItem.id, fileId }, apiKey);
            }
        }
    }, [completedMediaItems, apiKey, runInferenceForSource]);

    const getInferencesForSource = useCallback(
        (sourceId: string) => inferencesBySourceId.get(sourceId),
        [inferencesBySourceId]
    );

    /**
     * Handle uploaded files: add to media system (which uploads to Firebase Storage),
     * add to comparison store, and once upload completes run inference via api/infer.
     *
     * The flow:
     * 1. addMediaItems — MediaProvider uploads to Firebase Storage, shows in carousel
     * 2. Set as current media for immediate display
     * 3. Add to comparisonSources store with empty inferences
     * 4. Track the ID in pendingUploadIdsRef
     * 5. When the file appears in completedMediaItems (upload done), the effect above
     *    grabs uploadResult.filename and fires streamInfer via runInferenceForSource
     */
    const handleUploadedFiles = useCallback(
        async (files: File[]) => {
            for (const file of files) {
                const sourceId = uuidv4();

                // Prepare the file for the media system.
                // Do NOT set skipUpload — let MediaProvider upload to Firebase Storage.
                const mediaFile = file as MediaItemFile;
                mediaFile.id = sourceId;

                // Add to media system — this triggers upload to Firebase Storage
                const addedItems = await addMediaItems([mediaFile]);

                // Set as current media so it displays right away
                if (addedItems && addedItems.length > 0) {
                    const item = addedItems[0];
                    setCurrentMedia({ file: item, frame: item, frameSegment: 0 });
                }

                // Create a blob URL for the store's imageUrl/thumbUrl fields
                const blobUrl = URL.createObjectURL(file);
                blobUrlsRef.current.add(blobUrl);

                // Add to store so getInferencesForSource can find it
                const uploadedSource: TestSourceWithInference = {
                    id: sourceId,
                    owner: "",
                    name: file.name,
                    width: undefined,
                    height: undefined,
                    imageUrl: blobUrl,
                    thumbUrl: blobUrl,
                    inferences: {}
                };
                addUploadedSource(uploadedSource);

                // Track this ID — when upload completes, the effect fires inference
                pendingUploadIdsRef.current.add(sourceId);
            }
        },
        [addMediaItems, setCurrentMedia, addUploadedSource]
    );

    const contextValue = useMemo(
        () => ({ getInferencesForSource, isLoadingSources, handleUploadedFiles }),
        [getInferencesForSource, isLoadingSources, handleUploadedFiles]
    );

    return (
        <ModelComparisonContext.Provider value={contextValue}>
            {children}
        </ModelComparisonContext.Provider>
    );
}
